import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axios from "axios";
import API_URL from "../../config/ApiUrl";

export const getPurchaseReceives = createAsyncThunk(
  "purchaseReceive/getPurchaseReceives",
  async (payload, { rejectWithValue, dispatch }) => {
    try {
      dispatch(setActivePayload(payload));
      const filteredPayload = Object.fromEntries(
        Object.entries(payload).filter(
          ([, value]) => value !== null && value !== undefined && value !== "",
        ),
      );
      const params = new URLSearchParams();

      for (const [key, value] of Object.entries(filteredPayload)) {
        if (Array.isArray(value)) {
          value.forEach((item) => {
            params.append(key, item);
          });
        } else {
          params.append(key, value);
        }
      }
      const queryString = params.toString();
      const { data } = await axios.get(
        `${API_URL.PURCHASE_RECEIVE.SLUG}?${queryString}`,
      );
      const purchase_receive_list = data.data.list;
      const totalRecords = data.data.totalRecords;
      return { purchase_receive_list, data: { totalRecords } };
    } catch (error) {
      return rejectWithValue(error);
    }
  },
);

export const createPurchaseReceive = createAsyncThunk(
  "purchaseReceive/createPurchaseReceive",
  async (payload, { rejectWithValue }) => {
    try {
      const { data } = await axios.post(
        API_URL.PURCHASE_RECEIVE.CREATE,
        payload,
      );
      return data.data;
    } catch (error) {
      return rejectWithValue(error.data);
    }
  },
);

export const getPurchaseOrderForReceive = createAsyncThunk(
  "purchaseReceive/getPurchaseOrderForReceive",
  async (id, { rejectWithValue }) => {
    try {
      const { data } = await axios.get(API_URL.PURCHASE_ORDER.FOR_RECEIVE(id));
      return data.data;
    } catch (error) {
      return rejectWithValue(error.data);
    }
  },
);

export const getNextPRNumber = createAsyncThunk(
  "purchaseReceive/getNextPRNumber",
  async (_, { rejectWithValue }) => {
    try {
      const { data } = await axios.get(
        API_URL.PURCHASE_RECEIVE.GET_NEXT_PR_NUMBER,
      );
      return data.data;
    } catch (error) {
      return rejectWithValue(error.data);
    }
  },
);

export const getVendorDropdown = createAsyncThunk(
  "purchaseReceive/getVendorDropdown",
  async (payload, { rejectWithValue }) => {
    try {
      const filteredPayload = Object.fromEntries(
        Object.entries(payload).filter(
          ([, value]) => value !== null && value !== undefined && value !== "",
        ),
      );
      const params = new URLSearchParams();
      for (const [key, value] of Object.entries(filteredPayload)) {
        if (Array.isArray(value)) {
          value.forEach((item) => params.append(key, item));
        } else {
          params.append(key, value);
        }
      }
      const queryString = params.toString();
      const { data } = await axios.get(
        `${API_URL.VENDOR.DROPDOWN}?${queryString}`,
      );
      return data.data;
    } catch (error) {
      return rejectWithValue(error.data);
    }
  },
);

export const getPurchaseOrdersByVendor = createAsyncThunk(
  "purchaseReceive/getPurchaseOrdersByVendor",
  async (payload, { rejectWithValue }) => {
    try {
      const filteredPayload = Object.fromEntries(
        Object.entries(payload).filter(
          ([, value]) => value !== null && value !== undefined && value !== "",
        ),
      );
      const params = new URLSearchParams();
      for (const [key, value] of Object.entries(filteredPayload)) {
        if (Array.isArray(value)) {
          value.forEach((item) => params.append(key, item));
        } else {
          params.append(key, value);
        }
      }
      const queryString = params.toString();
      const { data } = await axios.get(
        `${API_URL.PURCHASE_ORDER.DROPDOWN}?${queryString}`,
      );
      return data.data;
    } catch (error) {
      return rejectWithValue(error.data);
    }
  },
);

export const updateInTransitToReceived = createAsyncThunk(
  "purchaseReceive/updateInTransitToReceived",
  async (payload, { rejectWithValue }) => {
    try {
      const { data } = await axios.put(
        API_URL.PURCHASE_RECEIVE.UPDATE_INTRANSIT_TO_RECEIVED,
        payload,
      );
      return data.data;
    } catch (error) {
      return rejectWithValue(error.data);
    }
  },
);

export const deletePurchaseReceive = createAsyncThunk(
  "purchaseReceive/deletePurchaseReceive",
  async (id, { rejectWithValue }) => {
    try {
      const { data } = await axios.delete(API_URL.PURCHASE_RECEIVE.DELETE(id));
      return data.data;
    } catch (error) {
      return rejectWithValue(error.data);
    }
  },
);

export const bulkImportPurchaseReceives = createAsyncThunk(
  "purchaseReceive/bulkImportPurchaseReceives",
  async ({ file, templateId }, { rejectWithValue, dispatch, getState }) => {
    try {
      const formData = new FormData();
      formData.append("file", file);

      const params = new URLSearchParams();
      if (templateId) params.append("templateId", templateId);

      const queryString = params.toString();
      const url = queryString
        ? `${API_URL.PURCHASE_RECEIVE.BULK_IMPORT}?${queryString}`
        : API_URL.PURCHASE_RECEIVE.BULK_IMPORT;

      const response = await axios.post(url, formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
        timeout: 5 * 60 * 1000, // 5 minutes for large files
      });
      const data = response.data;

      // // Check for business-level failure (API returns 200 but has validation errors)
      // if (data.data && data.data.errors && data.data.errors.length > 0) {
      //   return rejectWithValue(data.data);
      // }

      const { active_payload } = getState().purchaseReceive;
      if (active_payload) {
        dispatch(getPurchaseReceives(active_payload));
      }

      return data.data;
    } catch (error) {
      if (!error?.data && !error?.response) {
        return rejectWithValue({
          message:
            error?.message ||
            "Network error. The file may be too large or the server timed out.",
          errors: [],
        });
      }
      const errorPayload = error?.data?.data || error?.data || error;
      return rejectWithValue(errorPayload);
    }
  },
);

export const getBulkImportHistory = createAsyncThunk(
  "purchaseReceive/getBulkImportHistory",
  async (payload, { rejectWithValue }) => {
    try {
      const filteredPayload = Object.fromEntries(
        Object.entries(payload).filter(
          ([, value]) => value !== null && value !== undefined && value !== "",
        ),
      );
      const params = new URLSearchParams();

      for (const [key, value] of Object.entries(filteredPayload)) {
        if (Array.isArray(value)) {
          value.forEach((item) => {
            params.append(key, item);
          });
        } else {
          params.append(key, value);
        }
      }
      const queryString = params.toString();
      const { data } = await axios.post(
        `${API_URL.PURCHASE_RECEIVE.BULK_IMPORT_HISTORY}?${queryString}`,
        {},
        {
          headers: {
            "Content-Type": "application/json",
          },
        },
      );
      return data.data;
    } catch (error) {
      return rejectWithValue(error.data);
    }
  },
);

export const downloadPurchaseReceivePdf = createAsyncThunk(
  "purchaseReceive/downloadPurchaseReceivePdf",
  async (id, { rejectWithValue }) => {
    try {
      const response = await axios.get(
        API_URL.PURCHASE_RECEIVE.DOWNLOAD_PDF(id),
        {
          headers: {
            Accept: "application/pdf",
          },
          responseType: "blob", // ✅ IMPORTANT
        },
      );
      // Create blob URL from response
      const blob = new Blob([response.data], { type: "application/pdf" });
      const blobUrl = URL.createObjectURL(blob);

      // Open PDF in new tab
      const printWindow = window.open(blobUrl, "_blank");

      // Wait for PDF to load and trigger print dialog
      if (printWindow) {
        printWindow.onload = () => {
          printWindow.print();
        };
      }
    } catch (error) {
      return rejectWithValue(error.data);
    }
  },
);

// ===== Box Scanning & Invoice Thunks =====

export const getInvoiceGroups = createAsyncThunk(
  "purchaseReceive/getInvoiceGroups",
  async (payload, { rejectWithValue, dispatch }) => {
    try {
      dispatch(setActiveInvoicePayload(payload));
      const filteredPayload = Object.fromEntries(
        Object.entries(payload).filter(
          ([, value]) => value !== null && value !== undefined && value !== "",
        ),
      );
      const params = new URLSearchParams();
      for (const [key, value] of Object.entries(filteredPayload)) {
        if (Array.isArray(value)) {
          value.forEach((item) => params.append(key, item));
        } else {
          params.append(key, value);
        }
      }
      const queryString = params.toString();
      const { data } = await axios.get(
        `${API_URL.PURCHASE_RECEIVE.BY_INVOICE}?${queryString}`,
      );
      const invoice_group_list = data.data.list;
      const totalRecords = data.data.totalRecords;
      return { invoice_group_list, data: { totalRecords } };
    } catch (error) {
      return rejectWithValue(error);
    }
  },
);

export const getBoxProgress = createAsyncThunk(
  "purchaseReceive/getBoxProgress",
  async (purchaseReceiveId, { rejectWithValue }) => {
    try {
      const { data } = await axios.get(
        API_URL.PURCHASE_RECEIVE.BOX_PROGRESS(purchaseReceiveId),
      );
      return data.data;
    } catch (error) {
      return rejectWithValue(error.data);
    }
  },
);

export const getScanConfig = createAsyncThunk(
  "purchaseReceive/getScanConfig",
  async (vendorId, { rejectWithValue }) => {
    try {
      const params = vendorId ? `?vendorId=${vendorId}` : "";
      const { data } = await axios.get(
        `${API_URL.TENANT.SCAN_CONFIG}${params}`,
      );
      return data.data;
    } catch (error) {
      return rejectWithValue(error.data);
    }
  },
);

export const batchScanUpdate = createAsyncThunk(
  "purchaseReceive/batchScanUpdate",
  async (payload, { rejectWithValue }) => {
    try {
      const { data } = await axios.post(
        API_URL.PURCHASE_RECEIVE.BATCH_SCAN_UPDATE,
        payload,
      );
      return data.data;
    } catch (error) {
      return rejectWithValue(error.data);
    }
  },
);

export const markInvoiceAsReceived = createAsyncThunk(
  "purchaseReceive/markInvoiceAsReceived",
  async (payload, { rejectWithValue, dispatch, getState }) => {
    try {
      const { data } = await axios.put(
        API_URL.PURCHASE_RECEIVE.MARK_INVOICE_RECEIVED,
        payload,
      );
      return data.data;
    } catch (error) {
      return rejectWithValue(error.data);
    }
  },
);

export const getAvailableBins = createAsyncThunk(
  "purchaseReceive/getAvailableBins",
  async (purchaseReceiveId, { rejectWithValue }) => {
    try {
      const { data } = await axios.get(
        API_URL.PURCHASE_RECEIVE.AVAILABLE_BINS(purchaseReceiveId),
      );
      return data.data;
    } catch (error) {
      return rejectWithValue(error.data);
    }
  },
);

export const receiveBatch = createAsyncThunk(
  "purchaseReceive/receiveBatch",
  async (payload, { rejectWithValue, dispatch, getState }) => {
    try {
      const { data } = await axios.put(
        API_URL.PURCHASE_RECEIVE.RECEIVE_BATCH,
        payload,
      );
      // Refresh both PR-wise and invoice-wise lists
      const { active_payload, active_invoice_payload } =
        getState().purchaseReceive;
      if (active_payload) {
        dispatch(getPurchaseReceives(active_payload));
      }
      if (active_invoice_payload) {
        dispatch(getInvoiceGroups(active_invoice_payload));
      }
      return data.data;
    } catch (error) {
      return rejectWithValue(error.data);
    }
  },
);

export const getInvoiceAvailableBins = createAsyncThunk(
  "purchaseReceive/getInvoiceAvailableBins",
  async (invoiceNumber, { rejectWithValue }) => {
    try {
      const { data } = await axios.get(
        API_URL.PURCHASE_RECEIVE.INVOICE_AVAILABLE_BINS(invoiceNumber),
      );
      return data.data;
    } catch (error) {
      return rejectWithValue(error.data);
    }
  },
);

export const getInvoiceDetail = createAsyncThunk(
  "purchaseReceive/getInvoiceDetail",
  async (invoiceNumber, { rejectWithValue }) => {
    try {
      const { data } = await axios.get(
        API_URL.PURCHASE_RECEIVE.INVOICE_DETAIL(invoiceNumber),
      );
      return data.data;
    } catch (error) {
      return rejectWithValue(error.data);
    }
  },
);

// ===== Invoice-Level Verification Thunks =====

export const getInvoiceVerificationProgress = createAsyncThunk(
  "purchaseReceive/getInvoiceVerificationProgress",
  async (invoiceNumber, { rejectWithValue }) => {
    try {
      const { data } = await axios.get(
        API_URL.PURCHASE_RECEIVE.INVOICE_VERIFICATION_PROGRESS(invoiceNumber),
      );
      return data.data;
    } catch (error) {
      return rejectWithValue(error.data);
    }
  },
);

export const invoiceBatchScanUpdate = createAsyncThunk(
  "purchaseReceive/invoiceBatchScanUpdate",
  async (payload, { rejectWithValue }) => {
    try {
      const { data } = await axios.post(
        API_URL.PURCHASE_RECEIVE.INVOICE_BATCH_SCAN_UPDATE,
        payload,
      );
      return data.data;
    } catch (error) {
      return rejectWithValue(error.data);
    }
  },
);

// v2 — delta protocol with idempotency. Used for multi-user concurrent scanning.
// Payload: { invoiceNumber, boxScans: [...], itemScanEvents: [{ clientScanId, purchaseReceiveBoxId, qtyDelta, scannedAt }] }
export const invoiceBatchScanUpdateV2 = createAsyncThunk(
  "purchaseReceive/invoiceBatchScanUpdateV2",
  async (payload, { rejectWithValue }) => {
    try {
      const { data } = await axios.post(
        API_URL.PURCHASE_RECEIVE.INVOICE_BATCH_SCAN_UPDATE_V2,
        payload,
      );
      return data.data;
    } catch (error) {
      return rejectWithValue(error.data);
    }
  },
);

export const invoiceRejectBoxItem = createAsyncThunk(
  "purchaseReceive/invoiceRejectBoxItem",
  async (payload, { rejectWithValue }) => {
    try {
      const { data } = await axios.post(
        API_URL.PURCHASE_RECEIVE.INVOICE_REJECT_BOX_ITEM,
        payload,
      );
      return data.data;
    } catch (error) {
      return rejectWithValue(error.data);
    }
  },
);

export const invoiceShortageBoxItem = createAsyncThunk(
  "purchaseReceive/invoiceShortageBoxItem",
  async (payload, { rejectWithValue }) => {
    try {
      const { data } = await axios.post(
        API_URL.PURCHASE_RECEIVE.INVOICE_SHORTAGE_BOX_ITEM,
        payload,
      );
      return data.data;
    } catch (error) {
      return rejectWithValue(error.data);
    }
  },
);

export const invoiceMarkBoxMissing = createAsyncThunk(
  "purchaseReceive/invoiceMarkBoxMissing",
  async (payload, { rejectWithValue }) => {
    try {
      const { data } = await axios.post(
        API_URL.PURCHASE_RECEIVE.INVOICE_MARK_BOX_MISSING,
        payload,
      );
      return data.data;
    } catch (error) {
      return rejectWithValue(error.data);
    }
  },
);

export const completeInvoiceVerification = createAsyncThunk(
  "purchaseReceive/completeInvoiceVerification",
  async (payload, { rejectWithValue }) => {
    try {
      const { data } = await axios.post(
        API_URL.PURCHASE_RECEIVE.COMPLETE_INVOICE_VERIFICATION,
        payload,
      );
      return data.data;
    } catch (error) {
      return rejectWithValue(error.data);
    }
  },
);

// ===== Invoice-wise Receiving (Box Scanning at Receiving Stage) =====

export const startInvoiceReceiving = createAsyncThunk(
  "purchaseReceive/startInvoiceReceiving",
  async (payload, { rejectWithValue }) => {
    try {
      const { data } = await axios.post(
        API_URL.PURCHASE_RECEIVE.START_INVOICE_RECEIVING,
        payload,
      );
      return data.data;
    } catch (error) {
      return rejectWithValue(error.data);
    }
  },
);

export const completeInvoiceReceiving = createAsyncThunk(
  "purchaseReceive/completeInvoiceReceiving",
  async (payload, { rejectWithValue }) => {
    try {
      const { data } = await axios.post(
        API_URL.PURCHASE_RECEIVE.COMPLETE_INVOICE_RECEIVING,
        payload,
      );
      return data.data;
    } catch (error) {
      return rejectWithValue(error.data);
    }
  },
);

export const checkInvoiceDefaultBins = createAsyncThunk(
  "purchaseReceive/checkInvoiceDefaultBins",
  async (invoiceNumber, { rejectWithValue }) => {
    try {
      const { data } = await axios.get(
        API_URL.PURCHASE_RECEIVE.INVOICE_DEFAULT_BIN_CHECK(invoiceNumber),
      );
      return data.data;
    } catch (error) {
      return rejectWithValue(error.data);
    }
  },
);

export const setInvoiceDefaultBins = createAsyncThunk(
  "purchaseReceive/setInvoiceDefaultBins",
  async (payload, { rejectWithValue }) => {
    try {
      const { data } = await axios.post(
        API_URL.PURCHASE_RECEIVE.SET_INVOICE_DEFAULT_BINS,
        payload,
      );
      return data.data;
    } catch (error) {
      return rejectWithValue(error.data);
    }
  },
);

// ===== Item Batch Verify (QR Verification Client-Side Batching) =====

export const itemBatchVerify = createAsyncThunk(
  "purchaseReceive/itemBatchVerify",
  async (payload, { rejectWithValue }) => {
    try {
      const { data } = await axios.post(
        API_URL.PURCHASE_RECEIVE.ITEM_BATCH_VERIFY,
        payload,
      );
      return data.data;
    } catch (error) {
      return rejectWithValue(error.data);
    }
  },
);

const initialState = {
  purchase_receive_list: [],
  purchase_order_for_receive: null,
  current_values: null,
  active_payload: null,
  active_invoice_payload: null,
  next_pr_number: null,
  total_records: 0,
  bulk_import_history: [],
  // Box scanning & invoice state
  invoice_group_list: [],
  box_progress: null,
  scan_config: null,
  // Invoice-level verification
  invoice_verification_progress: null,
};

const purchaseReceiveSlice = createSlice({
  name: "purchaseReceive",
  initialState,
  reducers: {
    setCurrentValues(state, action) {
      state.current_values = action.payload;
    },
    setActivePayload(state, action) {
      state.active_payload = action.payload;
    },
    setActiveInvoicePayload(state, action) {
      state.active_invoice_payload = action.payload;
    },
    resetCurrentValue(state) {
      state.current_values = null;
      state.purchase_receive_list = [];
    },
    resetPurchaseOrderForReceive(state) {
      state.purchase_order_for_receive = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getPurchaseReceives.fulfilled, (state, action) => {
        state.purchase_receive_list = action.payload.purchase_receive_list;
        state.total_records = action.payload.data?.totalRecords || 0;
      })
      .addCase(getPurchaseOrderForReceive.fulfilled, (state, action) => {
        state.purchase_order_for_receive = action.payload;
      })
      .addCase(getNextPRNumber.fulfilled, (state, action) => {
        state.next_pr_number = action.payload;
      })
      .addCase(getBulkImportHistory.fulfilled, (state, action) => {
        state.bulk_import_history = action.payload.list || [];
      })
      .addCase(getInvoiceGroups.fulfilled, (state, action) => {
        state.invoice_group_list = action.payload.invoice_group_list;
      })
      .addCase(getBoxProgress.fulfilled, (state, action) => {
        state.box_progress = action.payload;
      })
      .addCase(getScanConfig.fulfilled, (state, action) => {
        state.scan_config = action.payload;
      })
      .addCase(getInvoiceVerificationProgress.fulfilled, (state, action) => {
        state.invoice_verification_progress = action.payload;
      });
  },
});

export const {
  setCurrentValues,
  setActivePayload,
  setActiveInvoicePayload,
  resetCurrentValue,
  resetPurchaseOrderForReceive,
} = purchaseReceiveSlice.actions;
export default purchaseReceiveSlice.reducer;
